import { AllergiesTemplate, type AllergyEntry } from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import type { RawPractitioner, RawSourceMetadata } from './_shared';
import { getProviderFacilityPdfEntry } from './pdfOverride';

type AllergiesDataKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_DATA; type: DocumentType.ALLERGIES }
>;

interface RawAllergy {
  id: string;
  name: string | null;
  recordedDate?: string;
  onsetStartDate?: string;
  onsetEndDate?: string;
  reactionManifestation?: string;
  practitioner?: RawPractitioner;
  dateUploaded?: string;
  sourceMetadata?: RawSourceMetadata;
  patientId?: string;
  allergyCodes?: string[];
  reactionManifestationCodes?: string[];
}

export interface AllergiesDataQueryData {
  allergies: RawAllergy[];
}

const mapEntry = (a: RawAllergy): AllergyEntry => ({
  id: a.id,
  name: a.name,
  reaction: a.reactionManifestation,
  recordedDate: a.recordedDate,
  onsetStart: a.onsetStartDate,
  onsetEnd: a.onsetEndDate,
  practitioner: a.practitioner,
});

export const adaptAllergiesData = (
  data: AllergiesDataQueryData,
  key: AllergiesDataKey,
): DocumentEntry | undefined => {
  const allergies = data?.allergies ?? [];
  if (allergies.length === 0) return undefined;

  const pdf = getProviderFacilityPdfEntry(key.sourceType, allergies, {
    title: key.name,
  });
  if (pdf) return pdf;

  const entries = allergies.map(mapEntry);
  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.allergyId ? `allergy-${key.allergyId}` : undefined,
    render: (ctx) => (
      <AllergiesTemplate
        entries={entries}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightEntryId={key.allergyId}
      />
    ),
  };
};
